/**
 * Customize PDP helpers: cover variant grouping/labels and draft selection maps (preselect, option slots).
 */

import {resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {normalizeCylindoFeatureToString, parseProcessedImageGroups} from '@amblique/king-living/app/utils/cylindo-params';
import {getOptionsGroupingUIProcessed, getProductEffectivePrice, getProductListPrice, isShelvesOptionsContainer, resolveShelvesPrimaryName} from '@amblique/king-living/app/utils/product-utils';

// --- Cover variants & groups -------------------------------------------------

/**
 * Fallback when `swatchName` is missing (SCAPI / legacy): reads `variationValues` (often internal IDs).
 *
 * @param {Object} variationValues
 * @returns {string}
 */
export const getCoverDisplayValueFromVariationValues = (variationValues) => {
  if (!variationValues || typeof variationValues !== 'object') {
    return '';
  }

  const coverValue = variationValues.cover ?? variationValues[Object.keys(variationValues)[0]];

  return coverValue != null ? String(coverValue) : '';
};

/**
 * Cover label for tiles: SFRA `c_allVariantsData` sets `swatchName` from variation `displayValue` (prefers `cover`).
 *
 * @param {Object} variant
 * @returns {string}
 */
export function getCoverAttributeDisplayNameForTile(variant) {
  return (
    variant?.swatchName ||
    variant?.c_processedData?.coverDisplayValue ||
    getCoverDisplayValueFromVariationValues(variant?.variationValues) ||
    variant?.name ||
    ''
  );
}

/**
 * Cover swatch URL — SFRA `c_allVariantsData[].swatchImageUrl`
 * (swatch → first `pimlyConfigurationImage` entry on variant).
 * No PWA fallback to processed image groups, master, PIM, or Cylindo.
 *
 * @param {Object} _product - Unused; kept for call-site compatibility
 * @param {Object} variant
 * @returns {string|null}
 */
export function getVariantSwatchImageUrl(_product, variant) {
  const url = variant?.swatchImageUrl;

  if (url != null && String(url).trim() !== '') {
    return String(url).trim();
  }

  return null;
}

/**
 * @param {*} raw
 * @returns {Array|null}
 */
function parseJsonAttribute(raw) {
  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * All cover variants (prefers `c_allVariantsData`; falls back to SCAPI `variants`, capped).
 *
 * @param {Object} product
 * @returns {Array}
 */
export const getAllCoverVariants = (product) => {
  if (!product) {
    return [];
  }

  const fromAllVariants = parseJsonAttribute(product.c_allVariantsData);

  if (fromAllVariants && fromAllVariants.length > 0) {
    return fromAllVariants;
  }

  return product.variants ?? [];
};

/**
 * Enriched cover variant for `coverId` from `c_allVariantsData` (or `variants` fallback).
 * @param {Object|null} product
 * @param {string|null} coverId
 */
export const getSelectedVariantProduct = (product, coverId) => {
  if (!product || !coverId) return null;

  const variants = getAllCoverVariants(product);

  return variants.find((v) => v.productId === coverId) ?? null;
};

/**
 * @param {Array} variants
 * @param {Object|null} [product]
 * @returns {Array}
 */
function buildCoverGroupsFromVariants(variants, product = null) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  const byItemType = new Map();

  for (const v of variants) {
    const processed = v?.c_processedData;
    const itemType = processed?.itemType ?? 'Other';

    if (!byItemType.has(itemType)) {
      byItemType.set(itemType, {byColor: new Map(), byCollection: new Map()});
    }

    const group = byItemType.get(itemType);
    const variantEntry = {
      productId: v.productId,
      variationValues: v.variationValues,
      orderable: v.orderable,
      ats: typeof v.ats === 'number' ? v.ats : null,
      perpetual: v.perpetual === true,
      leadTime: typeof v.leadTime === 'number' ? v.leadTime : null,
      name: v.name,
      swatchName: getCoverAttributeDisplayNameForTile(v),
      swatchImageUrl: product ? getVariantSwatchImageUrl(product, v) : null,
      swatchDescription: '',
      salePrice: v.salePrice ?? null,
      price: v.price ?? null,
      cylindoId: v.cylindoId ?? null,
      cylindoFeature: v.cylindoFeature ?? null,
      c_kingGuardApplicable: v.c_kingGuardApplicable === true,
      c_processedData: processed,
    };

    const color = processed?.refinementColor;

    if (color) {
      if (!group.byColor.has(color)) {
        group.byColor.set(color, []);
      }

      group.byColor.get(color).push(variantEntry);
    }

    const collectionName = processed?.materialName;

    if (collectionName) {
      if (!group.byCollection.has(collectionName)) {
        group.byCollection.set(collectionName, []);
      }

      group.byCollection.get(collectionName).push(variantEntry);
    }
  }

  return Array.from(byItemType.entries()).map(([itemType, g]) => ({
    itemType,
    byColor: Array.from(g.byColor.entries()).map(([color, vs]) => ({color, variants: vs})),
    byCollection: Array.from(g.byCollection.entries()).map(([collectionName, vs]) => ({
      collectionName,
      variants: vs,
    })),
  }));
}

/**
 * Case-insensitive match for cover tile marketing badges (e.g. Popular, POPULAR).
 *
 * @param {string|null|undefined} badgeText
 * @returns {boolean}
 */
function isPopularCoverBadge(badgeText) {
  return (badgeText ?? '').trim().toLowerCase().includes('popular');
}

/**
 * Stable partition: variants with a Popular badge first, preserving catalog order within each bucket.
 *
 * @param {Array} variants
 * @returns {Array}
 */
function sortCoverVariantsPopularFirst(variants) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return variants;
  }

  const popular = [];
  const rest = [];

  for (const variant of variants) {
    if (isPopularCoverBadge(variant?.c_processedData?.badgeText)) {
      popular.push(variant);
    } else {
      rest.push(variant);
    }
  }

  return popular.length > 0 ? [...popular, ...rest] : variants;
}

/**
 * Cover groups for the customize cover step: `c_coverGroupsData` legacy, else group from all variants.
 *
 * @param {Object} product
 * @returns {Array}
 */
export const getCoverGroups = (product) => {
  if (!product) return [];

  const legacyGrouped = parseJsonAttribute(product.c_coverGroupsData);
  const groups =
    legacyGrouped && legacyGrouped.length > 0 ?
      legacyGrouped :
      buildCoverGroupsFromVariants(getAllCoverVariants(product), product);

  return groups.map((group) => ({
    ...group,
    byColor: (group.byColor ?? []).map((row) => ({
      ...row,
      variants: sortCoverVariantsPopularFirst(row.variants),
    })),
    byCollection: (group.byCollection ?? []).map((row) => ({
      ...row,
      variants: sortCoverVariantsPopularFirst(row.variants),
    })),
  }));
};

// --- Selection maps (draft / preselect / option slots) ------------------------

/**
 * Filters recommendation product entries for UI lists.
 * Legacy SFRA payloads may still carry per-product `isHidden`; slot visibility uses OGU `isVisible`.
 *
 * @param {Array<{isHidden?: boolean}>} [products]
 * @returns {Array}
 */
export function filterVisibleOptionProducts(products) {
  if (!Array.isArray(products)) return [];

  return products.filter((p) => p && p.isHidden !== true);
}

/**
 * Auto-fill when slot is required and exactly one visible recommendation exists.
 *
 * @param {Object} [value] - OGU slot value from c_optionsGroupingUIProcessed
 * @returns {string|null}
 */
export function resolveRequiredSingleVisibleProductId(value) {
  if (value?.isVisible === false) {
    return null;
  }

  const visible = filterVisibleOptionProducts(value?.products);

  if (value?.isRequired === true && visible.length === 1 && visible[0]?.productId) {
    return visible[0].productId;
  }

  return null;
}

/**
 * Merges pre-selected product IDs into empty slot entries. Committed/user selections win
 * over preselect when a slot already has a value.
 *
 * Preselect when `value.isRequired === true` and the slot has exactly one visible
 * recommendation product — not `isPreselected` on the recommendation object.
 *
 * @param {Object} product - Master or editing product with c_optionsGroupingUIProcessed
 * @param {Object} baseSelections - SelectionsMap (cover + container slot arrays)
 * @param {Object} [options]
 * @param {boolean} [options.disablePreselect=false] - When true, suppresses the BM-driven
 *   isRequired auto-fill for option containers. Cover is always skipped (cover step
 *   has its own auto-select via `<SimpleCoverStep>`). Used by Simple PDP (KINDW-1244 /
 *   FSD §4.1) — "no option will be pre-selected by default" — so the user must manually
 *   pick each required slot before Add to bag enables.
 * @returns {Object} New selections map (shallow copy)
 */
export function mergePreselectedSelectionsFromProduct(product, baseSelections, {disablePreselect = false} = {}) {
  if (!product) {
    return baseSelections && typeof baseSelections === 'object' ? {...baseSelections} : {};
  }

  const base = baseSelections && typeof baseSelections === 'object' ? baseSelections : {};

  if (disablePreselect) {
    return {...base};
  }

  const processed = getOptionsGroupingUIProcessed(product);

  if (!processed || !Array.isArray(processed)) {
    return {...base};
  }

  const out = {...base};

  for (const container of processed) {
    if (!container || container.isHidden === true) continue;

    const cid = container.containerId;

    if (!cid || cid === 'cover') continue;

    const slots = Array.isArray(container.values) ? container.values : [];
    const slotSelections = [...(out[cid] || [])];

    slots.forEach((value, idx) => {
      if (slotSelections[idx] != null) return;

      const productId = resolveRequiredSingleVisibleProductId(value);

      if (productId) {
        const visible = filterVisibleOptionProducts(value?.products);
        const match = visible.find((p) => p.productId === productId) ?? visible[0];

        slotSelections[idx] = {
          productId,
          name: match?.name || null,
          recommendationName: value.typeName || container.containerName || null,
        };
      }
    });

    out[cid] = slotSelections;
  }

  return out;
}

/**
 * OGU recommendation entries expose `nameLine1`/`nameLine2`, SCAPI products the `c_` prefixed
 * attributes. Normalizes both onto the `c_` shape the label resolvers read.
 *
 * @param {Object|null|undefined} product
 * @returns {{c_nameLine1: string|null, c_nameLine2: string|null, name: string|null}}
 */
function normalizeOptionProductNameLines(product) {
  return {
    c_nameLine1: product?.c_nameLine1 ?? product?.nameLine1 ?? null,
    c_nameLine2: product?.c_nameLine2 ?? product?.nameLine2 ?? null,
    name: product?.name ?? null,
  };
}

/**
 * Comparison key for sibling matching: the label the shopper sees (`nameLine2, nameLine1`),
 * not the raw SCAPI `name` — distinct shelf options share a `name` while differing by
 * name line, so name matching picked the wrong sibling (KL-1120).
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} container
 * @returns {string}
 */
function resolveSiblingMatchKey(product, container) {
  const displayName = resolveOptionSlotSelectionName(normalizeOptionProductNameLines(product), container);

  return String(displayName ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Auto-fills empty sibling slots with matching products when user confirms a slot.
 * Only applies to slots that are currently empty.
 *
 * @param {Object} container - OGU container (containerId, values[])
 * @param {Array} row - Current slot row
 * @param {number} completedSlotIndex - Slot index user just committed
 * @param {Object} sourceProduct - Product from picker (display name drives matching)
 * @returns {Array} New slot row array
 */
export function applySiblingSlotMatchesByDisplayName(
  container,
  row,
  completedSlotIndex,
  sourceProduct
) {
  if (!container?.values?.length) {
    return row;
  }

  const sourceKey = resolveSiblingMatchKey(sourceProduct, container);

  if (!sourceKey) {
    return row;
  }

  const {values} = container;
  const out = [...row];

  for (let j = 0; j < values.length; j++) {
    if (j === completedSlotIndex) continue;
    if (out[j]?.productId) continue;

    const slotValue = values[j];
    const visible = filterVisibleOptionProducts(slotValue?.products);

    const match = visible.find((p) => p && resolveSiblingMatchKey(p, container) === sourceKey);

    if (!match?.productId) continue;

    const matchNameLines = normalizeOptionProductNameLines(match);
    const stubProduct = {
      id: match.productId,
      imageGroups: [],
      name: match.name || sourceProduct?.name || '',
      c_nameLine1: matchNameLines.c_nameLine1,
      c_nameLine2: matchNameLines.c_nameLine2,
    };

    out[j] = buildOptionSlotSelectionFromProduct(stubProduct, j, slotValue, container);
  }

  return out;
}

/**
 * Sum cover + option slot prices for a predefined-set card (flyout subtotal style).
 * Skips `optionalAddOns` and auto-selected (hidden) slots.
 * Uses `optionPriceMap` (SCAPI-fetched) when a slot has no `price` yet — same totals
 * collapsed vs expanded, before and after `selectSet`.
 *
 * @param {Object|null} selections - SelectionsMap
 * @param {Object.<string, number|null|undefined>} [optionPriceMap] - productId → effective price
 * @returns {number}
 */
export function sumSetCardSelectionPrices(selections, optionPriceMap) {
  if (!selections || typeof selections !== 'object') return 0;

  let total = 0;

  const cover = selections.cover;

  if (cover) {
    // Prefer the promotion-aware effective price from the fetched map (SCAPI
    // productPromotions), falling back to the cover's sale/list price.
    const raw =
      cover.productId && optionPriceMap && optionPriceMap[cover.productId] != null ?
        optionPriceMap[cover.productId] :
        (cover.salePrice ?? cover.price);
    const n = typeof raw === 'number' ? raw : Number(raw);

    if (Number.isFinite(n)) total += n;
  }

  Object.entries(selections).forEach(([key, slots]) => {
    if (key === 'cover' || key === 'optionalAddOns' || !Array.isArray(slots)) return;

    slots.forEach((slot) => {
      if (slot?.isAutoSelected) return;

      // Prefer the promotion-aware effective price from the fetched map so the
      // total reflects prices AFTER promotions; fall back to the slot's price.
      let p =
        slot?.productId && optionPriceMap && optionPriceMap[slot.productId] != null ?
          optionPriceMap[slot.productId] :
          slot?.price;

      if (p == null || !Number.isFinite(Number(p))) {
        p = slot?.price;
      }

      const num = typeof p === 'number' ? p : Number(p);

      if (Number.isFinite(num)) total += num;
    });
  });

  return total;
}

/**
 * Sum list prices (RRP / root price book) across cover + option slots for a set card.
 * Returns 0 when no list-price data exists (= no discount to show).
 *
 * @param {Object|null} selections - SelectionsMap
 * @param {Object.<string, number|null|undefined>} [listPriceMap] - productId → list price
 * @returns {number}
 */
export function sumSetCardSelectionListPrices(selections, listPriceMap) {
  if (!selections || typeof selections !== 'object') return 0;

  let total = 0;
  let hasListData = false;

  const cover = selections.cover;

  if (cover) {
    const listP = cover.price;
    const saleP = cover.salePrice;

    if (listP != null && saleP != null && listP > saleP) {
      total += listP;
      hasListData = true;
    } else {
      const raw = saleP ?? listP;
      const n = typeof raw === 'number' ? raw : Number(raw);

      if (Number.isFinite(n)) total += n;
    }
  }

  Object.entries(selections).forEach(([key, slots]) => {
    if (key === 'cover' || key === 'optionalAddOns' || !Array.isArray(slots)) return;

    slots.forEach((slot) => {
      if (slot?.isAutoSelected) return;

      let lp = slot?.listPrice;

      if (lp == null && slot?.productId && listPriceMap && listPriceMap[slot.productId] != null) {
        lp = listPriceMap[slot.productId];
      }

      if (lp != null && lp > (slot?.price ?? 0)) {
        total += lp;
        hasListData = true;
      } else {
        let p = slot?.price;
        const num = typeof p === 'number' ? p : Number(p);

        if (Number.isFinite(num)) total += num;
      }
    });
  });

  return hasListData ? total : 0;
}

function trimOptionProductNameLine(value) {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : String(value);

  return s.trim();
}

/**
 * Single-line label for option slot cards, flyout footer, and committed selections.
 * Prefers merchandising name lines (nameLine2, nameLine1) over SCAPI `name`.
 *
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export function formatOptionProductSelectionName(product) {
  if (!product) return '';

  const line1 = trimOptionProductNameLine(product.c_nameLine1);
  const line2 = trimOptionProductNameLine(product.c_nameLine2);
  const parts = [];

  if (line2) parts.push(line2);
  if (line1) parts.push(line1);

  if (parts.length > 0) return parts.join(', ');

  return product.name ?? '';
}

/**
 * KINDW-1558 — shelves slot name only. Other containers keep formatOptionProductSelectionName.
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} container
 * @returns {string}
 */
function resolveOptionSlotSelectionName(product, container) {
  if (isShelvesOptionsContainer(container)) {
    return resolveShelvesPrimaryName(product) ?? '';
  }

  return formatOptionProductSelectionName(product);
}

/**
 * Hero PIM carousel images for an option/recommendation product, mirroring
 * `buildLayerFromProduct` (gallery-resource-utils): the `large`, non-variation
 * group sourced from PIM. Returned so the committed slot can rebuild its gallery
 * layer (`buildLayersFromSelections`) for PIM-only options that carry no
 * `cylindoFeature` — without this the option image vanishes once the live-preview
 * product is cleared on flyout "Done" and the hero falls back to "Image not
 * available" (KINDW simple-PDP gallery revert).
 *
 * @param {Object|null|undefined} product
 * @returns {Array|null}
 */
function resolveOptionSlotPimImages(product) {
  const imageGroups = parseProcessedImageGroups(product);
  const heroGroup = imageGroups.find((g) => g.viewType === 'large' && !g.variationAttributes) ?? null;

  return heroGroup?.imageSource === 'pim' && heroGroup.images?.length > 0 ? heroGroup.images : null;
}

/**
 * Dedicated video URL for an option/recommendation product, mirroring
 * `getProductVideoUrl` (gallery-resource-utils). Stored on the committed slot so
 * the committed gallery layer keeps parity with the live-preview
 * `buildLayerFromProduct` — without this the option video vanishes once the
 * live-preview product is cleared on flyout "Done" (or after refresh) and the
 * hero falls back to the still image (KINDW simple-PDP video revert).
 *
 * Defined locally rather than imported from gallery-resource-utils, which already
 * imports from this module (avoids a circular dependency).
 *
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
function resolveOptionSlotVideoUrl(product) {
  const raw = product?.c_video ?? product?.video ?? null;
  const url = raw != null ? String(raw).trim() : '';

  return url || null;
}

/** One slot row in draftSelections[containerId]; built on Next from the resolved product. */
export function buildOptionSlotSelectionFromProduct(product, slotIndex, value, container) {
  const imageUrl = resolveProductThumbnailUrl(product);

  // cylindoFeature is stored so V2 gallery resource layering can combine features
  // from master + cover variant + all committed option slots without re-fetching products.
  const rawFeature = product?.c_cylindoFeature ?? product?.cylindoFeature ?? null;

  const effectivePrice = getProductEffectivePrice(product);
  const listPrice = getProductListPrice(product);

  return {
    imageUrl,
    name: resolveOptionSlotSelectionName(product, container),
    productId: product.id,
    price: effectivePrice,
    listPrice,
    recommendationName: value.typeName || container.containerName || null,
    recommendationTypeId: value.recommendationTypeId,
    slotIndex,
    cylindoFeature: normalizeCylindoFeatureToString(rawFeature),
    // PIM hero images so the committed gallery layer survives flyout close for
    // PIM-only options (parity with the live-preview `buildLayerFromProduct`).
    pimImages: resolveOptionSlotPimImages(product),
    // Dedicated video URL so a video option keeps showing in the hero after
    // "Done" and after refresh (video wins over PIM/Cylindo in the resolver).
    video: resolveOptionSlotVideoUrl(product),
    // Selection recency marker. When every slot is filled, the hero must reflect
    // the option the shopper most recently changed — not whichever slot happens to
    // sit last in catalog order. Layers are ordered by this in
    // buildLayersFromSelections so the latest choice wins the exclusive hero
    // (video/PIM last-layer-wins) and Cylindo feature merge (later wins per key).
    // Only re-stamped for the slot being (re)built, so untouched slots keep their
    // original recency across commits and refresh (persisted to session).
    selectedAt: Date.now(),
  };
}
